import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsApi } from '../lib/api-hooks';
import { getSnippet, activityLabel, buildSections } from './inbox-helpers';
import type { TicketRow, Section } from './inbox-helpers';
import { NewTicketModal } from '../components/ticket/NewTicketModal';
import { useAuth } from '../store/auth';
import { usePullToRefresh } from '../lib/usePullToRefresh';
import { formatSakNr, getInitials } from '../lib/format';
import TicketDetailPage, { type DraftState } from './TicketDetailPage';
import type { Prioritet, TicketStatus } from '../lib/types';
import {
  Mail,
  MessageCircle,
  Phone,
  Search,
  Plus,
  RefreshCw,
  Inbox,
  Archive,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

const FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'mine', label: 'Mine saker' },
  { key: 'unassigned', label: 'Ufordelte' },
] as const;

const CHANNEL_META: Record<string, { icon: typeof Mail; label: string }> = {
  EPOST: { icon: Mail, label: 'E-post' },
  MANUELL: { icon: Phone, label: 'Manuell' },
  CHAT: { icon: MessageCircle, label: 'Chat' },
};

const PRIORITY_RAIL: Record<Prioritet, string> = {
  KRITISK: '#e0483f',
  HOY: '#dd9a1f',
  NORMAL: '#c9cbd6',
  LAV: '#c9cbd6',
};

const PRIORITY_LABELS: Record<Prioritet, string> = {
  KRITISK: 'Kritisk',
  HOY: 'Høy',
  NORMAL: 'Normal',
  LAV: 'Lav',
};

const STATUS_LABELS: Record<TicketStatus, string> = {
  NY: 'Ny',
  UNDER_ARBEID: 'Under arbeid',
  VENTER_KUNDE: 'Venter kunde',
  VENTER_INTERNT: 'Venter internt',
  LOST: 'Løst',
  LUKKET: 'Lukket',
};

const STATUS_DOT: Record<TicketStatus, string> = {
  NY: '#3b4fd6',
  UNDER_ARBEID: '#dd9a1f',
  VENTER_KUNDE: '#8b5cf6',
  VENTER_INTERNT: '#f97316',
  LOST: '#22946a',
  LUKKET: '#9ca0aa',
};

/* ---------- Time helpers ---------- */
function formatClock(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  }
  const diffDay = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDay < 7) {
    return d.toLocaleDateString('nb-NO', { weekday: 'short' });
  }
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit' });
}

function waitingMinutes(iso: string, tick: number): number {
  return Math.max(0, Math.round((tick - new Date(iso).getTime()) / 60000));
}

function formatWaiting(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}t ${m}m` : `${h}t`;
}

function waitingTier(mins: number): 'cool' | 'warm' | 'hot' {
  if (mins >= 60) return 'hot';
  if (mins >= 20) return 'warm';
  return 'cool';
}

/* ---------- Component ---------- */
export default function InboxPage({ filter: propFilter }: { filter?: 'mine' }) {
  const { id: selectedId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>(propFilter ?? 'all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [inboxScope, setInboxScope] = useState<'active' | 'closed'>('active');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [tick, setTick] = useState(Date.now());

  // Master-detail: /inbox/:id eller /mine/:id
  const basePath = propFilter === 'mine' ? '/mine' : '/inbox';
  const isArchive = inboxScope === 'closed';

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Utkast per sak overlever bytte mellom embedded saker i samme økt
  const draftCacheRef = useRef(new Map<string, Record<'customer' | 'internal', DraftState>>());

  // Live clock for waiting chips
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (propFilter === 'mine') setFilter('mine');
  }, [propFilter]);

  // Advar før reload dersom det finnes usendte utkast i minne-cachen
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      for (const drafts of draftCacheRef.current.values()) {
        if (
          drafts.customer.text.trim() ||
          drafts.customer.files.length ||
          drafts.internal.text.trim() ||
          drafts.internal.files.length
        ) {
          e.preventDefault();
          return;
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['tickets', user?.id, filter, search, inboxScope, page],
    queryFn: () =>
      ticketsApi.list({
        filter: filter === 'all' ? undefined : filter,
        q: search || undefined,
        inboxScope,
        page,
      }),
    refetchInterval: 30_000,
  });

  const { pullDistance, refreshing, handlers: pullHandlers } = usePullToRefresh(async () => {
    await refetch();
    qc.invalidateQueries({ queryKey: ['tickets'] });
  });

  const tickets: TicketRow[] = data?.tickets ?? [];

  // Grupper saker i seksjoner etter hva som krever oppmerksomhet.
  // Ulest aktivitet havner alltid øverst – det er "nytt siden sist".
  const sections = useMemo<Section[]>(() => buildSections(tickets, user?.id), [tickets, user?.id]);

  // Kun seksjoner som faktisk rendres er navigerbare med J/K
  const orderedTickets = useMemo(
    () => sections.flatMap((s) => (s.collapsible && !showClosed ? [] : s.items)),
    [sections, showClosed],
  );
  const unreadCount = tickets.filter((t) => t.ulest).length;

  // Scroll listen til toppen ved side-/filter-/arkivbytte
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [page, filter, inboxScope, search]);

  // Scroll valgt rad inn i syne kun hvis den ikke allerede er synlig,
  // slik at periodisk polling ikke forårsaker rykkete scrolling.
  useEffect(() => {
    if (!selectedId) return;
    const el = rowRefs.current[selectedId];
    const wrap = listRef.current;
    if (!el || !wrap) return;
    const rect = el.getBoundingClientRect();
    const parent = wrap.getBoundingClientRect();
    if (rect.top < parent.top || rect.bottom > parent.bottom) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedId]);

  // Keyboard shortcuts – kun når fokus er i listepanelet eller på body,
  // aldri mens brukeren skriver eller en modal/svar-boks er åpen.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName ?? '';
      if (
        ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(tag) ||
        target?.isContentEditable ||
        target?.closest('[role="dialog"]')
      ) {
        return;
      }
      const inList =
        target === document.body ||
        target === listRef.current ||
        (target != null && (listRef.current?.contains(target) ?? false));
      if (!inList) return;

      if (e.key === 'Escape') {
        if (showNewTicket) setShowNewTicket(false);
        else searchRef.current?.blur();
        return;
      }
      if (showNewTicket) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (e.key.toLowerCase() === 'n' && !selectedId) {
        e.preventDefault();
        setShowNewTicket(true);
        return;
      }
      if (detailBusy) return;
      if (['j', 'ArrowDown', 'k', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        const idx = orderedTickets.findIndex((t) => t.id === selectedId);
        const dir = e.key === 'j' || e.key === 'ArrowDown' ? 1 : -1;
        const next = orderedTickets[Math.min(Math.max(idx + dir, 0), orderedTickets.length - 1)];
        if (next) navigate(`${basePath}/${next.id}`);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [orderedTickets, selectedId, navigate, showNewTicket, basePath, detailBusy]);

  const openTicket = useCallback((id: string) => {
    navigate(`${basePath}/${id}`);
  }, [navigate, basePath]);

  const doRefresh = useCallback(async () => {
    await refetch();
    qc.invalidateQueries({ queryKey: ['tickets'] });
  }, [refetch, qc]);

  const renderRow = (t: TicketRow) => {
    const channelMeta = CHANNEL_META[t.kanal] ?? CHANNEL_META.MANUELL;
    const ChannelIcon = channelMeta.icon;
    const mins = waitingMinutes(t.opprettet, tick);
    const tier = waitingTier(mins);
    const showWaiting = !t.assigneeId && t.status !== 'LOST' && t.status !== 'LUKKET';
    const snippet = getSnippet(t);
    return (
      <div
        key={t.id}
        ref={(el) => { rowRefs.current[t.id] = el; }}
        className={`inbox-row inbox-focusable ${selectedId === t.id ? 'selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-current={selectedId === t.id ? true : undefined}
        onClick={() => openTicket(t.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openTicket(t.id);
          }
        }}
      >
        <div className="inbox-row-rail" style={{ background: PRIORITY_RAIL[t.prioritet] }} />
        <div className="inbox-row-body">
          <div className="inbox-channel" title={channelMeta.label}>
            <ChannelIcon size={15} strokeWidth={2} />
          </div>
          <div className="inbox-main">
            <div className="inbox-main-top">
              <span className="inbox-saknr">{formatSakNr(t.sakNr)}</span>
              <span
                className="inbox-status-dot"
                style={{ background: STATUS_DOT[t.status] }}
              />
              <span className="inbox-status-text">{STATUS_LABELS[t.status]}</span>
              <span className="inbox-customer">
                {t.customer?.navn || t.customer?.epost || 'Ukjent kunde'}
              </span>
            </div>
            <div className="inbox-subject" style={{ fontWeight: t.ulest ? 700 : 500 }}>
              {t.ulest && <span className="inbox-unread-dot" title="Ulest" />}
              {t.tittel}
            </div>
            {snippet && <div className="inbox-snippet">{snippet}</div>}
            <div className="inbox-tags">
              {(t.prioritet === 'HOY' || t.prioritet === 'KRITISK') && (
                <span className={`inbox-prio-tag ${t.prioritet}`}>
                  {PRIORITY_LABELS[t.prioritet]}
                </span>
              )}
              {t.kategori && (
                <span className="inbox-tag">{t.kategori}</span>
              )}
              {t._count?.messages ? (
                <span className="inbox-msg-count">
                  <MessageCircle size={11} strokeWidth={2} />
                  {t._count.messages}
                </span>
              ) : null}
            </div>
          </div>
          <div className="inbox-meta">
            <span className="inbox-time" title="Sist aktivitet">
              {formatClock(t.sistAktivitet ?? t.oppdatert)}
            </span>
            <span className="inbox-activity-label">{activityLabel(t)}</span>
            {showWaiting ? (
              <span className={`inbox-waiting ${tier}`}>
                <span className="inbox-dot" />
                {formatWaiting(mins)}
              </span>
            ) : t.assignee ? (
              <span className="inbox-avatar" title={t.assignee.navn}>
                {t.assignee.avatarUrl ? (
                  <img src={t.assignee.avatarUrl} alt={t.assignee.navn} />
                ) : (
                  getInitials(t.assignee.navn)
                )}
              </span>
            ) : (
              <span className="inbox-unassigned-pill">Ikke tildelt</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="inbox-root">
      <style>{`
        .inbox-root {
          --bg: #f4f5f8;
          --surface: #ffffff;
          --ink: #14161b;
          --ink-dim: #676c78;
          --ink-faint: #9ca0aa;
          --line: #e7e8ee;
          --line-strong: #c9cbd6;
          --accent: #3b4fd6;
          --accent-ink: #ffffff;
          --accent-soft: #ebeefd;
          --urgent: #e0483f;
          --urgent-soft: #fbeceb;
          --amber: #dd9a1f;
          --amber-soft: #fbf1de;
          --ok: #22946a;
          --ok-soft: #e6f4ee;
          --radius: 14px;
          font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
          background: var(--bg);
          color: var(--ink);
          height: calc(100dvh - 80px - env(safe-area-inset-bottom));
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .inbox-root { height: calc(100dvh - 61px); }
        }
        .dark .inbox-root {
          --bg: #0f172a;
          --surface: #1e293b;
          --ink: #f1f5f9;
          --ink-dim: #cbd5e1;
          --ink-faint: #94a3b8;
          --line: #334155;
          --line-strong: #475569;
          --accent: #818cf8;
          --accent-ink: #0f172a;
          --accent-soft: #312e81;
          --urgent: #f87171;
          --urgent-soft: #7f1d1d;
          --amber: #fbbf24;
          --amber-soft: #78350f;
          --ok: #34d399;
          --ok-soft: #064e3b;
        }
        .dark .inbox-row:hover { background: var(--bg); }
        .inbox-root * { box-sizing: border-box; }

        .inbox-focusable:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
          border-radius: 8px;
        }

        /* Topbar */
        .inbox-topbar {
          background: var(--surface);
          border-bottom: 1px solid var(--line);
          padding: 14px 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .inbox-brand { display: flex; align-items: center; gap: 10px; }
        .inbox-brand-mark {
          width: 30px; height: 30px; border-radius: 9px;
          background: var(--ink);
          display: flex; align-items: center; justify-content: center;
        }
        .inbox-brand-mark svg { color: var(--accent-ink); }
        .inbox-brand-name { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
        .inbox-brand-sub { font-size: 12px; color: var(--ink-faint); }

        .inbox-newbtn {
          display: flex; align-items: center; gap: 7px;
          background: var(--accent); color: var(--accent-ink);
          border: none; border-radius: 10px;
          padding: 8px 12px 8px 10px;
          font-size: 13.5px; font-weight: 600;
          cursor: pointer;
          transition: background 120ms ease, transform 120ms ease;
        }
        .inbox-newbtn:hover { background: #2f41c2; }
        .inbox-newbtn:active { transform: scale(0.97); }
        .inbox-newbtn kbd {
          font-family: ui-monospace, monospace;
          font-size: 10.5px;
          background: rgba(255,255,255,0.18);
          padding: 1px 5px;
          border-radius: 4px;
        }

        /* Control bar */
        .inbox-controlbar {
          background: var(--surface);
          border-bottom: 1px solid var(--line);
          padding: 10px 22px 14px;
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }
        .inbox-segment {
          display: flex; gap: 2px;
          background: var(--bg);
          padding: 3px; border-radius: 10px;
        }
        .inbox-segment button {
          border: none; background: transparent;
          padding: 6px 12px; border-radius: 8px;
          font-size: 13px; font-weight: 600; color: var(--ink-dim);
          cursor: pointer; display: flex; align-items: center; gap: 6px;
          transition: background 120ms ease, color 120ms ease;
        }
        .inbox-segment button.active { background: var(--surface); color: var(--ink); box-shadow: 0 1px 2px rgba(20,22,27,0.08); }
        .inbox-segment button .inbox-count {
          font-family: ui-monospace, monospace;
          font-size: 10.5px; padding: 1px 5px; border-radius: 999px;
          background: var(--line); color: var(--ink-dim);
        }
        .inbox-segment button.active .inbox-count { background: var(--accent-soft); color: var(--accent); }

        .inbox-search {
          position: relative; flex: 1; min-width: 200px; max-width: 340px;
        }
        .inbox-search input {
          width: 100%;
          background: var(--bg);
          border: 1px solid transparent;
          border-radius: 10px;
          padding: 8px 34px 8px 34px;
          font-size: 13.5px;
          color: var(--ink);
        }
        .inbox-search input::placeholder { color: var(--ink-faint); }
        .inbox-search input:focus {
          outline: none; border-color: var(--accent); background: var(--surface);
          box-shadow: 0 0 0 3px var(--accent-soft);
        }
        .inbox-search .inbox-icon-left {
          position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
          color: var(--ink-faint); pointer-events: none;
        }
        .inbox-search .inbox-kbd-right {
          position: absolute; right: 9px; top: 50%; transform: translateY(-50%);
          font-family: ui-monospace, monospace; font-size: 10.5px;
          color: var(--ink-faint); background: var(--surface); border: 1px solid var(--line);
          padding: 1px 5px; border-radius: 4px; pointer-events: none;
        }

        .inbox-refresh {
          margin-left: auto;
          display: flex; align-items: center; gap: 6px;
          background: transparent; border: 1px solid var(--line);
          border-radius: 10px; padding: 7px 11px;
          font-size: 12.5px; font-weight: 600; color: var(--ink-dim);
          cursor: pointer;
        }
        .inbox-refresh:hover { border-color: var(--line-strong); color: var(--ink); }
        .inbox-refresh:disabled { opacity: 0.5; cursor: default; }
        .inbox-refresh svg.spinning { animation: inbox-spin 0.8s linear infinite; }
        @keyframes inbox-spin { to { transform: rotate(360deg); } }

        /* Pull to refresh */
        .inbox-pull {
          display: flex; align-items: center; justify-content: center;
          overflow: hidden; color: var(--ink-faint); background: var(--bg);
          transition: height 160ms ease;
        }

        /* List */
        .inbox-list-wrap { flex: 1; overflow-y: auto; background: var(--bg); }
        .inbox-row {
          display: flex; align-items: stretch; gap: 0;
          background: var(--surface);
          border-bottom: 1px solid var(--line);
          cursor: pointer;
          position: relative;
          transition: background 120ms ease;
        }
        .inbox-row:hover { background: #fafbff; }
        .inbox-row.selected { background: var(--accent-soft); }
        .inbox-row-rail { width: 4px; flex-shrink: 0; }
        .inbox-row-body {
          flex: 1; display: flex; align-items: flex-start; gap: 12px;
          padding: 13px 18px 13px 14px; min-width: 0;
        }
        .inbox-channel {
          width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: var(--bg); color: var(--ink-dim);
        }
        .inbox-row.selected .inbox-channel { background: var(--surface); }
        .inbox-main { flex: 1; min-width: 0; }
        .inbox-main-top { display: flex; align-items: baseline; gap: 8px; }
        .inbox-saknr { font-family: ui-monospace, monospace; font-size: 11px; color: var(--ink-faint); font-weight: 500; }
        .inbox-customer { font-size: 12.5px; color: var(--ink-dim); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .inbox-status-dot { width: 7px; height: 7px; border-radius: 999px; flex-shrink: 0; }
        .inbox-status-text { font-size: 11px; color: var(--ink-faint); white-space: nowrap; }
        .inbox-activity-label { font-size: 11px; color: var(--ink-dim); white-space: nowrap; }
        .inbox-subject {
          font-size: 14.5px; margin: 2px 0 3px; color: var(--ink);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-weight: 500;
        }
        .inbox-snippet {
          font-size: 12.5px; color: var(--ink-faint);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .inbox-tags { display: flex; gap: 6px; margin-top: 7px; flex-wrap: wrap; align-items: center; }
        .inbox-tag {
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.01em;
          padding: 2px 7px; border-radius: 999px;
          background: var(--bg); color: var(--ink-dim);
        }
        .inbox-prio-tag {
          font-size: 10.5px; font-weight: 600;
          padding: 2px 7px; border-radius: 999px;
        }
        .inbox-prio-tag.KRITISK { background: var(--urgent-soft); color: var(--urgent); }
        .inbox-prio-tag.HOY { background: var(--amber-soft); color: var(--amber); }
        .inbox-prio-tag.NORMAL { background: var(--accent-soft); color: var(--accent); }
        .inbox-prio-tag.LAV { background: var(--bg); color: var(--ink-faint); }
        .inbox-msg-count {
          font-size: 10.5px; color: var(--ink-faint); display: flex; align-items: center; gap: 3px;
        }
        .inbox-meta {
          display: flex; flex-direction: column; align-items: flex-end; gap: 7px;
          flex-shrink: 0; padding-left: 8px; min-width: 92px;
        }
        .inbox-time { font-family: ui-monospace, monospace; font-size: 11px; color: var(--ink-faint); }
        .inbox-waiting {
          font-family: ui-monospace, monospace; font-size: 10.5px; font-weight: 600;
          padding: 2px 7px; border-radius: 999px; display: flex; align-items: center; gap: 4px;
        }
        .inbox-waiting.cool { background: var(--bg); color: var(--ink-dim); }
        .inbox-waiting.warm { background: var(--amber-soft); color: var(--amber); }
        .inbox-waiting.hot { background: var(--urgent-soft); color: var(--urgent); }
        .inbox-waiting .inbox-dot { width: 5px; height: 5px; border-radius: 999px; background: currentColor; }
        .inbox-waiting.hot .inbox-dot { animation: inbox-pulse 1.4s ease-in-out infinite; }
        @keyframes inbox-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
        .inbox-avatar {
          width: 24px; height: 24px; border-radius: 999px;
          background: var(--ink); color: #fff;
          font-family: ui-monospace, monospace; font-size: 10px; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .inbox-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .inbox-unassigned-pill {
          font-size: 10.5px; font-weight: 600; color: var(--ink-faint);
          border: 1px dashed var(--line-strong); border-radius: 999px; padding: 2px 8px;
        }

        /* Empty state */
        .inbox-empty {
          height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 10px; color: var(--ink-faint); padding: 40px;
        }
        .inbox-empty-icon {
          width: 52px; height: 52px; border-radius: 14px; background: var(--surface);
          border: 1px solid var(--line);
          display: flex; align-items: center; justify-content: center; color: var(--ink-faint);
        }
        .inbox-empty-title { font-size: 14px; font-weight: 600; color: var(--ink-dim); }
        .inbox-empty-sub { font-size: 12.5px; text-align: center; max-width: 260px; }

        /* Loading */
        .inbox-loading {
          height: 100%; display: flex; align-items: center; justify-content: center;
          color: var(--ink-faint); font-size: 14px;
        }
        .inbox-loading .inbox-pulse { animation: inbox-pulse 1.5s ease-in-out infinite; }

        /* Footer */
        .inbox-footer {
          background: var(--surface); border-top: 1px solid var(--line);
          padding: 9px 22px; font-size: 11.5px; color: var(--ink-faint);
          display: flex; justify-content: space-between; align-items: center; gap: 10px;
          flex-wrap: wrap;
        }
        .inbox-footer-mono { font-family: ui-monospace, monospace; }
        .inbox-pager { display: flex; align-items: center; gap: 6px; }
        .inbox-page-btn {
          display: flex; align-items: center; gap: 4px;
          background: transparent; border: 1px solid var(--line);
          border-radius: 8px; padding: 4px 9px;
          font-size: 11.5px; font-weight: 600; color: var(--ink-dim);
          cursor: pointer;
        }
        .inbox-page-btn:hover:not(:disabled) { border-color: var(--line-strong); color: var(--ink); }
        .inbox-page-btn:disabled { opacity: 0.45; cursor: default; }

        .inbox-hint-row { display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--ink-faint); }
        .inbox-hint-row kbd {
          font-family: ui-monospace, monospace; font-size: 10px;
          background: var(--bg); border: 1px solid var(--line); padding: 1px 5px; border-radius: 4px;
        }

        /* Master-detail layout */
        .inbox-body { flex: 1; display: flex; min-height: 0; overflow: hidden; }
        .inbox-list-pane { flex: 1; display: flex; flex-direction: column; min-width: 0; min-height: 0; }
        .inbox-detail-pane { display: none; }
        .inbox-detail-pane > * { min-height: 0; }

        @media (min-width: 900px) {
          .inbox-list-pane {
            flex: 0 0 clamp(360px, 30vw, 440px);
            border-right: 1px solid var(--line);
          }
          .inbox-detail-pane {
            display: flex; flex-direction: column; flex: 1; min-width: 0; min-height: 0;
            overflow: hidden; background: var(--surface);
          }
        }
        @media (max-width: 899px) {
          .inbox-list-pane.has-detail { display: none; }
          .inbox-detail-pane.open {
            display: flex; flex: 1; flex-direction: column; min-height: 0; overflow: hidden;
          }
        }

        /* Seksjoner */
        .inbox-section-header {
          display: flex; align-items: center; gap: 8px;
          padding: 12px 18px 6px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; color: var(--ink-faint);
          position: sticky; top: 0; z-index: 2;
          background: var(--bg);
        }
        .inbox-section-header.unread { color: var(--accent); }
        .inbox-section-count {
          font-family: ui-monospace, monospace; font-size: 10px; font-weight: 600;
          background: var(--line); color: var(--ink-dim);
          padding: 1px 6px; border-radius: 999px;
        }
        .inbox-section-header.unread .inbox-section-count {
          background: var(--accent-soft); color: var(--accent);
        }
        .inbox-section-toggle {
          border: none; background: var(--bg); cursor: pointer;
          display: flex; align-items: center; gap: 6px; width: 100%;
          padding: 12px 18px 6px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; color: var(--ink-faint);
          position: sticky; top: 0; z-index: 2; text-align: left;
        }
        .inbox-section-toggle:hover { color: var(--ink-dim); }
        .inbox-section-toggle svg { transition: transform 120ms ease; }
        .inbox-section-toggle svg.rotated { transform: rotate(90deg); }

        /* Ulest-indikator */
        .inbox-unread-dot {
          display: inline-block; width: 7px; height: 7px; border-radius: 999px;
          background: var(--accent); margin-right: 6px; vertical-align: 1px;
        }
        .inbox-tag-new {
          background: var(--accent-soft) !important; color: var(--accent) !important;
        }

        @media (max-width: 640px) {
          .inbox-snippet { display: none; }
          .inbox-meta { min-width: 60px; }
          .inbox-hint-row { display: none; }
          .inbox-topbar { padding: 12px 14px; }
          .inbox-controlbar { padding: 8px 14px 12px; }
          .inbox-footer { padding: 9px 14px; }
          .inbox-pager { width: 100%; justify-content: space-between; }
        }

        @media (prefers-reduced-motion: reduce) {
          .inbox-row, .inbox-newbtn, .inbox-refresh svg, .inbox-waiting .inbox-dot { animation: none !important; transition: none !important; }
        }
      `}</style>

      {/* Topbar */}
      <div className="inbox-topbar">
        <div className="inbox-brand">
          <div className="inbox-brand-mark">
            <Inbox size={16} strokeWidth={2.4} />
          </div>
          <div>
            <div className="inbox-brand-name">{isArchive ? 'Arkiv' : 'Innboks'}</div>
            <div className="inbox-brand-sub">
              {data?.total ?? 0} {isArchive ? 'arkiverte' : 'åpne'} saker
            </div>
          </div>
        </div>
        <button className="inbox-newbtn inbox-focusable" onClick={() => setShowNewTicket(true)}>
          <Plus size={15} strokeWidth={2.6} />
          Ny sak
          <kbd>N</kbd>
        </button>
      </div>

      {/* Master-detail: liste til venstre, åpen sak til høyre */}
      <div className="inbox-body">
        <div className={`inbox-list-pane ${selectedId ? 'has-detail' : ''}`}>
          {/* Controls */}
          <div className="inbox-controlbar">
        <div className="inbox-segment">
          {FILTERS.filter((f) => !(isArchive && f.key === 'unassigned')).map((f) => (
            <button
              key={f.key}
              className={`inbox-focusable ${filter === f.key ? 'active' : ''}`}
              onClick={() => {
                setFilter(f.key);
                setPage(1);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="inbox-search">
          <Search size={14} className="inbox-icon-left" />
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder={isArchive ? 'Søk i arkivet…' : 'Søk i åpne saker…'}
            aria-label={isArchive ? 'Søk i arkivet' : 'Søk i åpne saker'}
            className="inbox-focusable"
          />
          {!search && <span className="inbox-kbd-right">/</span>}
        </div>

        <button
          className="inbox-refresh inbox-focusable"
          style={{ marginLeft: 0 }}
          onClick={() => {
            const next = isArchive ? 'active' : 'closed';
            setInboxScope(next);
            setShowClosed(next === 'closed');
            if (next === 'closed' && filter === 'unassigned') setFilter('all');
            setPage(1);
          }}
        >
          <Archive size={13} />
          {isArchive ? 'Til innboksen' : 'Arkiv'}
        </button>

        <div className="inbox-hint-row">
          <kbd>J</kbd>
          <kbd>K</kbd> naviger
          <span style={{ margin: '0 2px' }}>·</span>
          <kbd>↵</kbd> åpne
        </div>

        <button className="inbox-refresh inbox-focusable" onClick={doRefresh} disabled={refreshing}>
          <RefreshCw size={13} className={refreshing ? 'spinning' : ''} />
          {refreshing ? 'Oppdaterer…' : 'Oppdater'}
        </button>
      </div>

      {/* Pull to refresh visual */}
      <div className="inbox-pull" style={{ height: pullDistance }}>
        <RefreshCw size={16} className={pullDistance > 56 || refreshing ? 'spinning' : ''} />
      </div>

      {/* List */}
      <div
        className="inbox-list-wrap"
        ref={listRef}
        {...pullHandlers}
      >
        {isLoading ? (
          <div className="inbox-loading">
            <span className="inbox-pulse">Laster saker…</span>
          </div>
        ) : isError && !data ? (
          <div className="inbox-empty">
            <div className="inbox-empty-icon">
              <Inbox size={22} strokeWidth={1.6} />
            </div>
            <div className="inbox-empty-title">Klarte ikke å laste saker</div>
            <button className="inbox-refresh" onClick={() => refetch()}>
              <RefreshCw size={13} />
              Prøv igjen
            </button>
          </div>
        ) : orderedTickets.length === 0 ? (
          <div className="inbox-empty">
            <div className="inbox-empty-icon">
              <Inbox size={22} strokeWidth={1.6} />
            </div>
            <div className="inbox-empty-title">
              {search
                ? 'Ingen treff'
                : filter === 'unassigned'
                ? 'Alle saker er tildelt'
                : filter === 'mine'
                ? 'Ingen saker på deg'
                : 'Innboksen er tom'}
            </div>
            <div className="inbox-empty-sub">
              {search
                ? `Fant ingen saker som matcher «${search}». Prøv et annet søkeord.`
                : filter === 'unassigned'
                ? 'Godt jobbet — ingenting venter på en eier akkurat nå.'
                : filter === 'mine'
                ? 'Saker du blir tildelt dukker opp her.'
                : isArchive
                ? 'Løste og lukkede saker havner her.'
                : 'Nye saker fra e-post, chat og telefon havner her.'}
            </div>
          </div>
        ) : (
          sections.map((section) => {
            if (section.items.length === 0) return null;
            const isCollapsed = section.collapsible && !showClosed;
            return (
              <div key={section.key}>
                {section.collapsible ? (
                  <button
                    type="button"
                    className="inbox-section-toggle"
                    aria-expanded={showClosed}
                    onClick={() => setShowClosed((v) => !v)}
                  >
                    <ChevronRight size={13} className={showClosed ? 'rotated' : ''} />
                    {section.label}
                    <span className="inbox-section-count">{section.items.length}</span>
                  </button>
                ) : (
                  <div className={`inbox-section-header ${section.key === 'unread' ? 'unread' : ''}`}>
                    {section.label}
                    <span className="inbox-section-count">{section.items.length}</span>
                  </div>
                )}
                {!isCollapsed && section.items.map(renderRow)}
              </div>
            );
          })
        )}
          </div>

          {/* Footer */}
          {data && (
            <div className="inbox-footer">
              <span className="inbox-footer-mono">
                Side {data.page} av {Math.max(data.totalPages, 1)} · {data.total} saker
              </span>
              <span>{unreadCount} uleste på denne siden</span>
              <div className="inbox-pager">
                <button
                  className="inbox-page-btn"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={12} />
                  Forrige
                </button>
                <button
                  className="inbox-page-btn"
                  disabled={page >= data.totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Neste
                  <ChevronRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail-pane (master-detail) */}
        <div className={`inbox-detail-pane ${selectedId ? 'open' : ''}`}>
          {selectedId ? (
            <TicketDetailPage
              key={selectedId}
              embedded
              backTo={basePath}
              draftCache={draftCacheRef.current}
              onComposeChange={setDetailBusy}
            />
          ) : (
            <div className="inbox-empty">
              <div className="inbox-empty-icon">
                <Inbox size={22} strokeWidth={1.6} />
              </div>
              <div className="inbox-empty-title">Velg en sak</div>
              <div className="inbox-empty-sub">
                Klikk på en sak i listen, eller bruk J/K for å navigere.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New ticket modal */}
      <NewTicketModal open={showNewTicket} onClose={() => setShowNewTicket(false)} />
    </div>
  );
}
